import { useTranslation } from 'react-i18next'
import Map, { Marker } from 'react-map-gl'
import {
  Alert,
  AlertTitle,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogProps,
  DialogTitle,
  Stack,
  Typography,
  useTheme
} from '@mui/material'
import { ActionRequest, Device, GeoCoordinate } from '../types'
import { humanizeTimestamp } from '../utils/date'
import { getDevice } from '../services/deviceService'
import { useEffect, useState } from 'react'
import LoadingState from './LoadingState'
import { grey } from '@mui/material/colors'
import DeviceDetachButton from './DeviceDetachButton'
import ActionRequestRejectButton from './ActionRequestRejectButton'
import DeviceDetailsAccordion from './DeviceDetailsAccordion'
import { mapBoxAPIKey } from '../utils/apiKey'
import 'mapbox-gl/dist/mapbox-gl.css'

interface ActionRequestDeviceLocationChangedDialogProps extends Omit<
  DialogProps,
  'children' | 'onClose'
> {
  onClose: () => void
  actionRequest: ActionRequest
  afterAccept?: (actionRequest: ActionRequest) => void
  afterReject?: (actionRequest: ActionRequest) => void
  afterDelete?: () => void
}

export default function ActionRequestDeviceLocationChangedDialog({
  actionRequest,
  open,
  onClose,
  afterAccept: _afterAccept,
  afterReject,
  afterDelete: _afterDelete,
  ...props
}: ActionRequestDeviceLocationChangedDialogProps) {
  /**
   * The translate function.
   */
  const { t } = useTranslation()

  /**
   * The theme.
   */
  const theme = useTheme()

  /**
   * The device state.
   */
  const [device, setDevice] = useState<Device>()

  /**
   * The loading state.
   */
  const [loading, setLoading] = useState<boolean>(false)

  /**
   * The error state.
   */
  const [error, setError] = useState<unknown>()

  /**
   * The current location of the device.
   */
  const currentLocation: GeoCoordinate | null = device?.locationHistory?.[0]
    ? {
        lat: parseFloat(device.locationHistory[0].lat.toString()),
        long: parseFloat(device.locationHistory[0].lng.toString())
      }
    : null

  /**
   * The previous location of the device.
   */
  const previousLocation: GeoCoordinate | null = device?.locationHistory?.[1]
    ? {
        lat: parseFloat(device.locationHistory[1].lat.toString()),
        long: parseFloat(device.locationHistory[1].lng.toString())
      }
    : null

  /**
   * Load the device.
   */
  async function loadDevice(name: string) {
    try {
      setLoading(true)
      setError(undefined)
      setDevice(await getDevice(name))
    } catch (error) {
      setError(error)
    } finally {
      setLoading(false)
    }
  }

  /**
   * Handle the reject action.
   */
  function handleReject(actionRequest: ActionRequest) {
    afterReject?.(actionRequest)
    onClose()
  }

  /**
   * Load the device when the action request's requester changes.
   */
  useEffect(() => {
    if (actionRequest.requesterId !== null) {
      loadDevice(actionRequest.requesterId)
    }
  }, [actionRequest.requesterId])

  return (
    <Dialog open={open} maxWidth="md" fullWidth {...props} onClose={onClose}>
      <DialogTitle>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">
            {t('actionRequest:deviceLocationForDeviceHasChanged', {
              device: device?.asset?.name ?? device?.name ?? actionRequest.requesterId
            })}
          </Typography>
          <Typography>{humanizeTimestamp(actionRequest.createdAt)}</Typography>
        </Stack>
      </DialogTitle>
      <DialogContent sx={{ pb: 0 }}>
        {loading ? (
          <LoadingState />
        ) : error ? (
          <Alert severity="error">{t('somethingWentWrong')}</Alert>
        ) : (
          device && (
            <>
              {currentLocation && (
                <Map
                  mapboxAccessToken={mapBoxAPIKey}
                  initialViewState={{
                    latitude: currentLocation.lat,
                    longitude: currentLocation.long,
                    zoom: 10
                  }}
                  mapStyle="mapbox://styles/mapbox/streets-v9"
                  style={{ width: '100%', height: 560 }}
                >
                  {previousLocation && (
                    <Marker
                      color={grey[400]}
                      longitude={previousLocation.long}
                      latitude={previousLocation.lat}
                    />
                  )}

                  <Marker
                    color={theme.palette.success.light}
                    longitude={currentLocation.long}
                    latitude={currentLocation.lat}
                  />
                </Map>
              )}

              <Alert
                severity="warning"
                sx={{ mt: 4 }}
                action={
                  <Stack direction="row" gap={2}>
                    <ActionRequestRejectButton
                      actionRequest={actionRequest}
                      variant="outlined"
                      label={t('iDoNot')}
                      color="inherit"
                      size="small"
                      afterSubmit={handleReject}
                    />
                    <DeviceDetachButton
                      device={device}
                      startIcon={false}
                      variant="outlined"
                      label={t('yes')}
                      size="small"
                      color="warning"
                    />
                  </Stack>
                }
              >
                <AlertTitle sx={{ mt: 0, mb: 0 }}>
                  {t('doYouWantToDetachThisDeviceFromProject')}
                </AlertTitle>
              </Alert>

              <DeviceDetailsAccordion device={device} />
            </>
          )
        )}
      </DialogContent>
      <DialogActions>
        <Button variant="text" color="inherit" onClick={onClose}>
          {t('cancel')}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
